<template>
  <aside class="conversation-panel" :class="{ minimized: props.minimized }" :aria-label="$t('app.experience.panel.ariaLabel')">
    <div class="panel-header">
      <div class="panel-title">
        <span class="panel-step">{{ $t('app.experience.detail.step', { number: props.index + 1 }) }} · {{ content.name }}</span>
        <span class="panel-persona">{{ $t('app.experience.panel.title', { name: content.persona }) }}</span>
      </div>
      <Button class="outline secondary icon icon-left m" icon="bi-chevron-double-right" :label="$t('app.experience.panel.minimize')" @click="minimize" />
      <Button class="secondary m" :label="$t('app.experience.panel.endStep')" :disabled="streamStatus === 'error'" @click="emits('endStep')" />
    </div>
    <div class="panel-body">
      <div class="video-area">
        <div class="video-placeholder">
          <template v-if="streamStatus === 'ready' && streamUrl">
            <iframe :src="streamUrl" :title="$t('app.experience.panel.ariaLabel')" allow="microphone; camera; autoplay; fullscreen" allowfullscreen></iframe>
            <span class="live-tag"><span class="pulse-dot"></span>{{ $t('app.experience.panel.live') }}</span>
          </template>
          <template v-else>
            <div class="avatar">{{ content.persona.charAt(0).toUpperCase() }}</div>
            <div class="identity">
              <span class="name">{{ content.persona }}</span>
              <span v-if="content.role" class="role">{{ content.role }}</span>
            </div>
            <span v-if="streamStatus === 'loading'" class="session-status">{{ $t('app.experience.panel.loading') }}</span>
            <template v-else>
              <span class="session-status">{{ $t(`app.experience.panel.errors.${streamErrorKind ?? 'generic'}`) }}</span>
              <Button v-if="canRetryStream" class="primary m" :label="$t('app.experience.panel.retry')" @click="retryStream" />
            </template>
          </template>
        </div>
      </div>
      <div class="mic-hint">
        <span class="mic-icon"><i class="bi bi-mic"></i></span>
        <span>{{ $t('app.experience.panel.micHint') }}</span>
      </div>
    </div>
  </aside>

  <button v-if="props.minimized" type="button" class="conversation-pill" @click="emits('restore')">
    <span class="pulse-dot"></span>
    <span>{{ $t('app.experience.panel.pill') }} · <strong>{{ content.persona }}</strong></span>
    <span class="pill-action">{{ $t('app.experience.panel.restore') }}<i class="bi bi-chevron-double-left"></i></span>
  </button>
</template>

<script setup lang="ts">
import type { AssessmentSituation } from '@/types/campaign';
import { useSituationContent } from './useSituationContent';
import { useStreamingSession } from './useStreamingSession';

const props = defineProps<{
  situation: AssessmentSituation,
  participationId: string,
  index: number,
  minimized: boolean,
}>();

const emits = defineEmits(['minimize', 'restore', 'close', 'endStep', 'conversationEnded', 'conversationError']);

const content = useSituationContent(() => props.situation);
const {
  url: streamUrl,
  status: streamStatus,
  errorKind: streamErrorKind,
  canRetry: canRetryStream,
  retry: retryStream,
} = useStreamingSession(() => props.participationId, () => props.situation.id, {
  onEnded: () => emits('conversationEnded'),
  onError: (message) => emits('conversationError', message),
});

// The conversation could not be launched: nothing is running, so the panel is closed instead of minimized
function minimize() {
  emits(streamStatus.value === 'error' ? 'close' : 'minimize');
}
</script>

<style lang="scss" scoped>
@import "@/assets/style/variables.scss";

.conversation-panel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 30;
  display: flex;
  flex-direction: column;
  width: min(1400px, 100%);

  background-color: var(--white-color);
  border-left: 1px solid var(--light-grey-color);
  box-shadow: -12px 0 32px rgba(0, 0, 0, .12);

  animation: experience-slide-in .32s cubic-bezier(.2, .8, .2, 1);
  transition: transform .3s cubic-bezier(.2, .8, .2, 1);

  &.minimized {
    transform: translateX(calc(100% + 40px));
  }
}

.panel-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px 14px 20px;
  border-bottom: 1px solid var(--light-grey-color);

  .panel-title {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex-grow: 1;
    min-width: 0;
  }

  .panel-step {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: .4px;
    color: var(--grey-50-color);
  }

  .panel-persona {
    font-size: 15px;
    font-weight: 700;
  }
}

.panel-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex-grow: 1;
  min-height: 0;
  padding: 16px;
  background-color: var(--light-white-color);
}

// Available space for the video, measured through container queries
.video-area {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-grow: 1;
  min-height: 0;
  container-type: size;
}

// Largest 4/3 frame fitting in .video-area
.video-placeholder {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  width: min(100cqw, 100cqh * 4 / 3);
  aspect-ratio: 4 / 3;
  border-radius: 10px;
  overflow: hidden;
  background-color: #141414;
  color: $white-color;

  iframe {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: 0;
  }

  .session-status {
    font-size: 13px;
    color: $white-800-color;
  }

  .live-tag {
    position: absolute;
    top: 12px;
    right: 12px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
  }

  .avatar {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 112px;
    height: 112px;
    border-radius: 50%;
    background-color: $secondary-600-color;
    font-size: 41px;
    font-weight: 600;
  }

  .identity {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;

    .name {
      font-size: 17px;
      font-weight: 600;
    }

    .role {
      font-size: 12px;
      color: $white-800-color;
    }
  }
}

.mic-hint {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid var(--light-grey-color);
  border-radius: 8px;
  background-color: var(--white-color);
  font-size: 12px;
  line-height: 1.45;

  .mic-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background-color: $green-50-color;
    color: $green-700-color;
  }
}

.conversation-pill {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 31;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  height: 52px;
  padding: 0 8px 0 18px;
  border: none;
  border-radius: 26px;
  background-color: $secondary-color;
  color: $white-color;
  box-shadow: 0 8px 24px rgba(0, 0, 0, .2);
  font-family: inherit;
  font-size: 13px;
  cursor: pointer;

  strong {
    font-weight: 600;
  }

  .pill-action {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 36px;
    padding: 0 14px;
    border-radius: 18px;
    background-color: $primary-color;
    color: $secondary-color;
    font-weight: 600;
  }
}

.pulse-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #FF5A4E;
  animation: experience-pulse 1.4s ease-in-out infinite;
}

@keyframes experience-slide-in {
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
}

@keyframes experience-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: .3; }
}
</style>
